/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * SuccessRevealDialog — configures what a visitor is shown after a button saves.
 *
 * Some columns only acquire a value at the moment of the write: a passcode, an
 * opaque token. The visitor needs them and nothing else can carry them back, so
 * the button says which to show as text and which to turn into a QR.
 *
 * It is a DIALOG rather than more controls in the button card because the card
 * is already long. Configured or not, the card grows by one summary line.
 *
 * Edits a DRAFT. Nothing reaches the stored configuration until Save, so
 * Cancel and Escape genuinely discard.
 */

import React, { useEffect, useRef, useState } from 'react';

/* The visitor's modal is 390px wide on a phone; wording longer than this has
 * nowhere to go. These caps live in the UI only — the server does not enforce a
 * length, so a hand-written configuration can still overflow the modal. */
const MAX_TITLE = 60;
const MAX_BUTTON = 30;
const MAX_FIELD_LABEL = 30;

/** Columns this button actually writes — the only rows a reveal can read. */
export function writtenTables(button) {
  const seen = [];
  ((button.action || {}).operations || []).forEach(op => {
    if (op.table && !seen.includes(op.table)) seen.push(op.table);
  });
  return seen;
}

function draftFrom(reveal, tables) {
  const fields = {};
  const labels = {};
  ((reveal || {}).fields || []).forEach(f => {
    if (!f.column) return;
    fields[f.column] = true;
    labels[f.column] = f.label || f.column;
  });
  return {
    title: (reveal || {}).title || '',
    table: (reveal || {}).table || tables[0] || '',
    qr: ((reveal || {}).qr || {}).column || '',
    showLabel: ((reveal || {}).qr || {}).label || 'Show QR code',
    hideLabel: ((reveal || {}).qr || {}).hide_label || 'Hide QR code',
    doneLabel: (reveal || {}).done_label || 'Done',
    fields,
    labels,
  };
}

export default function SuccessRevealDialog({ button, columnsOf, reveal, onSave, onCancel }) {
  const tables = writtenTables(button);
  const [draft, setDraft] = useState(() => draftFrom(reveal, tables));
  const dialogRef = useRef(null);

  useEffect(() => {
    if (dialogRef.current) dialogRef.current.focus();
    function onKeyDown(e) { if (e.key === 'Escape') onCancel(); }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const columns = (columnsOf(draft.table) || []).map(c => c.name).filter(Boolean);
  const set = changes => setDraft(d => ({ ...d, ...changes }));

  function chooseTable(name) {
    // Columns belong to a table, so nothing chosen for the old one survives.
    set({ table: name, qr: '', fields: {}, labels: {} });
  }

  function save() {
    const chosen = columns
      .filter(c => draft.fields[c] && c !== draft.qr)
      .map(c => ({ column: c, label: draft.labels[c] || c }));

    const next = { table: draft.table, fields: chosen };
    if (draft.title.trim()) next.title = draft.title.trim();
    if (draft.doneLabel.trim() && draft.doneLabel.trim() !== 'Done') {
      next.done_label = draft.doneLabel.trim();
    }
    if (draft.qr) {
      next.qr = { column: draft.qr };
      if (draft.showLabel.trim()) next.qr.label = draft.showLabel.trim();
      if (draft.hideLabel.trim()) next.qr.hide_label = draft.hideLabel.trim();
    }
    onSave(next);
  }

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center
                 justify-content-center p-3"
      style={{ background: 'rgba(24,22,44,.42)', zIndex: 1080, overflowY: 'auto' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="bg-white rounded-3 shadow-lg d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-label="Configure the pass"
        tabIndex={-1}
        ref={dialogRef}
        style={{ maxWidth: 620, width: '100%', maxHeight: '86vh' }}
      >
        <div className="px-3 py-3 border-bottom d-flex justify-content-between align-items-start">
          <div>
            <div className="fw-bold">Configure the pass</div>
            <div className="small text-muted">
              What the visitor is shown after this button saves.
            </div>
          </div>
          <button type="button" className="btn-close" aria-label="Close" onClick={onCancel} />
        </div>

        <div className="px-3 py-3 overflow-auto">

          <div className="text-uppercase small fw-semibold text-muted border-bottom pb-1 mb-2">
            What it says
          </div>

          <div className="mb-2">
            <label className="form-label small fw-semibold mb-1" htmlFor="rv-title">
              Heading above the pass
            </label>
            <input
              id="rv-title" className="form-control form-control-sm"
              maxLength={MAX_TITLE}
              value={draft.title}
              placeholder="Your event pass"
              onChange={e => set({ title: e.target.value })}
            />
          </div>

          <div className="row g-2 mb-3">
            <div className="col-sm-4">
              <label className="form-label small fw-semibold mb-1" htmlFor="rv-show">
                Reveal button
              </label>
              <input
                id="rv-show" className="form-control form-control-sm"
                maxLength={MAX_BUTTON}
                value={draft.showLabel}
                onChange={e => set({ showLabel: e.target.value })}
              />
            </div>
            <div className="col-sm-4">
              <label className="form-label small fw-semibold mb-1" htmlFor="rv-hide">
                After revealing
              </label>
              <input
                id="rv-hide" className="form-control form-control-sm"
                maxLength={MAX_BUTTON}
                value={draft.hideLabel}
                onChange={e => set({ hideLabel: e.target.value })}
              />
            </div>
            <div className="col-sm-4">
              <label className="form-label small fw-semibold mb-1" htmlFor="rv-done">
                Dismiss button
              </label>
              <input
                id="rv-done" className="form-control form-control-sm"
                maxLength={MAX_BUTTON}
                value={draft.doneLabel}
                onChange={e => set({ doneLabel: e.target.value })}
              />
            </div>
          </div>

          <div className="text-uppercase small fw-semibold text-muted border-bottom pb-1 mb-2">
            What it shows
          </div>

          <div className="mb-2">
            <label className="form-label small fw-semibold mb-1" htmlFor="rv-table">
              Which record
            </label>
            <select
              id="rv-table" className="form-select form-select-sm"
              value={draft.table}
              onChange={e => chooseTable(e.target.value)}
            >
              {tables.length === 0 && <option value="">— this button saves nothing yet —</option>}
              {tables.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="form-text">
              Only tables this button saves into. The pass can show a row this click
              created, and nothing else.
            </div>
          </div>

          <div className="mb-2">
            <div className="small fw-semibold mb-1">Show as text</div>
            {columns.length === 0 && (
              <div className="form-text text-danger">No columns on that table.</div>
            )}
            {columns.map(col => {
              const isQr = col === draft.qr;
              const ticked = Boolean(draft.fields[col]) && !isQr;
              return (
                <div className="d-flex gap-2 align-items-center mb-1" key={col}>
                  <div className="form-check mb-0" style={{ minWidth: 190 }}>
                    <input
                      className="form-check-input" type="checkbox"
                      id={`rv-col-${col}`}
                      aria-label={`Show ${col} as text`}
                      checked={ticked}
                      disabled={isQr}
                      onChange={e => set({ fields: { ...draft.fields, [col]: e.target.checked } })}
                    />
                    <label
                      className={`form-check-label small${isQr ? ' text-muted' : ''}`}
                      htmlFor={`rv-col-${col}`}
                    >
                      {col}
                    </label>
                  </div>
                  <input
                    className="form-control form-control-sm"
                    aria-label={`Label for ${col}`}
                    maxLength={MAX_FIELD_LABEL}
                    placeholder={col}
                    disabled={!ticked}
                    value={draft.labels[col] ?? ''}
                    onChange={e => set({ labels: { ...draft.labels, [col]: e.target.value } })}
                  />
                </div>
              );
            })}
            <div className="form-text">
              Tick a column, then name it as the visitor should read it.
            </div>
          </div>

          <div className="mb-1">
            <label className="form-label small fw-semibold mb-1" htmlFor="rv-qr">
              Turn into a QR code
            </label>
            <select
              id="rv-qr" className="form-select form-select-sm"
              value={draft.qr}
              onChange={e => set({ qr: e.target.value })}
            >
              <option value="">— none —</option>
              {columns.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="form-text">
              Hidden behind the reveal button until the visitor asks for it.
            </div>
            {draft.qr && draft.fields[draft.qr] && (
              <div className="form-text text-warning-emphasis">
                Greyed out above — a value inside a QR must not also be printed as text
                beside it, or one photograph gives away both.
              </div>
            )}
          </div>
        </div>

        <div className="px-3 py-2 border-top d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-sm btn-primary" onClick={save}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
